import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import Layout from '../../components/Layout';
import { sideBarItems } from '../../utils';
import SubscriptionService from '../../services/admin/SubscriptionService';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const initialFilters = {
  category: '',
  fromDate: '',
  toDate: ''
};

const initialExpenseForm = {
  title: '',
  category: '',
  amount: '',
  description: '',
  date: ''
};

function SubscriptionExpenses() {
  const [filters, setFilters] = useState(initialFilters);
  const [expenses, setExpenses] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const [expenseForm, setExpenseForm] = useState(initialExpenseForm);
  const [editingExpenseId, setEditingExpenseId] = useState('');
  const [deleteExpenseId, setDeleteExpenseId] = useState('');

  useEffect(() => {
    loadExpenses().catch(() => {});
  }, [page]);

  const getError = (err) => err?.response?.data?.error || 'Operation failed';

  const buildParams = () => {
    const params = { page, limit };
    if (filters.category.trim()) params.category = filters.category.trim();
    if (filters.fromDate) params.fromDate = filters.fromDate;
    if (filters.toDate) params.toDate = filters.toDate;
    return params;
  };

  const loadExpenses = async (forceFirstPage = false) => {
    setLoading(true);
    try {
      const nextPage = forceFirstPage ? 1 : page;
      const res = await SubscriptionService.getExpenses({ ...buildParams(), page: nextPage });
      const payload = res?.data || {};
      setExpenses(payload?.data || []);
      setTotalPages(payload?.pagination?.totalPages || 1);
      if (forceFirstPage) setPage(1);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoading(false);
    }
  };

  const onApplyFilters = async () => {
    await loadExpenses(true);
  };

  const onResetFilters = async () => {
    setFilters(initialFilters);
    setPage(1);
    setLoading(true);
    try {
      const res = await SubscriptionService.getExpenses({ page: 1, limit });
      const payload = res?.data || {};
      setExpenses(payload?.data || []);
      setTotalPages(payload?.pagination?.totalPages || 1);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoading(false);
    }
  };

  const onSaveExpense = async () => {
    if (!expenseForm.title.trim() || !expenseForm.category.trim() || expenseForm.amount === '') {
      toast.error('Title, category and amount are required');
      return;
    }
    const amount = Number(expenseForm.amount);
    if (Number.isNaN(amount) || amount < 0) {
      toast.error('Amount must be non-negative');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: expenseForm.title.trim(),
        category: expenseForm.category.trim(),
        amount,
        description: expenseForm.description.trim(),
        date: expenseForm.date || undefined
      };

      if (editingExpenseId) {
        await SubscriptionService.updateExpense(editingExpenseId, payload);
        toast.success('Expense updated');
      } else {
        await SubscriptionService.createExpense(payload);
        toast.success('Expense added');
      }

      setExpenseForm(initialExpenseForm);
      setEditingExpenseId('');
      await loadExpenses();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoading(false);
    }
  };

  const onDeleteExpense = async () => {
    if (!deleteExpenseId) return;
    setLoading(true);
    try {
      await SubscriptionService.deleteExpense(deleteExpenseId);
      toast.success('Expense deleted');
      setDeleteExpenseId('');
      await loadExpenses();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoading(false);
    }
  };

  const onEditExpense = (item) => {
    setEditingExpenseId(item._id);
    setExpenseForm({
      title: item.title || '',
      category: item.category || '',
      amount: item.amount ?? '',
      description: item.description || '',
      date: item.date ? new Date(item.date).toISOString().split('T')[0] : ''
    });
  };

  const resetExpenseForm = () => {
    setEditingExpenseId('');
    setExpenseForm(initialExpenseForm);
  };

  const exportExpenseCsv = () => {
    if (!expenses.length) {
      toast.error('No expense data to export');
      return;
    }
    const headers = ['Title', 'Category', 'Amount', 'Date', 'Description'];
    const rows = expenses.map((item) => ([
      item.title || 'N/A',
      item.category || 'N/A',
      Number(item.amount || 0).toFixed(2),
      item.date ? new Date(item.date).toLocaleDateString() : 'N/A',
      item.description || 'N/A'
    ]));
    const csv = [headers, ...rows]
      .map((row) => row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `subscription-expenses-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportExpensePdf = () => {
    if (!expenses.length) {
      toast.error('No expense data to export');
      return;
    }
    const rows = expenses.map((item) => `
      <tr>
        <td>${item.title || 'N/A'}</td>
        <td>${item.category || 'N/A'}</td>
        <td>BDT ${Number(item.amount || 0).toFixed(2)}</td>
        <td>${item.date ? new Date(item.date).toLocaleDateString() : 'N/A'}</td>
        <td>${item.description || 'N/A'}</td>
      </tr>
    `).join('');
    const w = window.open('', '_blank');
    w.document.write(`
      <html><head><title>Subscription Expense Report</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 16px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #ddd; padding: 8px; font-size: 12px; }
        th { background: #f5f5f5; }
      </style></head>
      <body>
        <h2>Subscription Expense Report</h2>
        <table>
          <thead><tr><th>Title</th><th>Category</th><th>Amount</th><th>Date</th><th>Description</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </body></html>
    `);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <Layout title={sideBarItems.expenses.key}>
      <div className="w-full py-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Expense Logs</h1>

        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input className="w-full px-3 py-2 border rounded-md" placeholder="Category" value={filters.category} onChange={(e) => setFilters((prev) => ({ ...prev, category: e.target.value }))} />
            <input className="w-full px-3 py-2 border rounded-md" type="date" value={filters.fromDate} onChange={(e) => setFilters((prev) => ({ ...prev, fromDate: e.target.value }))} />
            <input className="w-full px-3 py-2 border rounded-md" type="date" value={filters.toDate} onChange={(e) => setFilters((prev) => ({ ...prev, toDate: e.target.value }))} />
            <div className="flex gap-2">
              <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50" onClick={onApplyFilters} disabled={loading}>Apply</button>
              <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50" onClick={onResetFilters} disabled={loading}>Reset</button>
            </div>
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
            <input className="w-full px-3 py-2 border rounded-md" placeholder="Title" value={expenseForm.title} onChange={(e) => setExpenseForm((prev) => ({ ...prev, title: e.target.value }))} />
            <input className="w-full px-3 py-2 border rounded-md" placeholder="Category" value={expenseForm.category} onChange={(e) => setExpenseForm((prev) => ({ ...prev, category: e.target.value }))} />
            <input className="w-full px-3 py-2 border rounded-md" placeholder="Amount" type="number" value={expenseForm.amount} onChange={(e) => setExpenseForm((prev) => ({ ...prev, amount: e.target.value }))} />
            <input className="w-full px-3 py-2 border rounded-md" type="date" value={expenseForm.date} onChange={(e) => setExpenseForm((prev) => ({ ...prev, date: e.target.value }))} />
            <input className="w-full px-3 py-2 border rounded-md" placeholder="Description (optional)" value={expenseForm.description} onChange={(e) => setExpenseForm((prev) => ({ ...prev, description: e.target.value }))} />
          </div>

          <div className="mb-2 flex gap-2">
            <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50" onClick={onSaveExpense} disabled={loading}>{editingExpenseId ? 'Update Expense' : 'Add Expense'}</button>
            {editingExpenseId && <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200" onClick={resetExpenseForm}>Cancel</button>}
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
            <h2 className="text-lg font-semibold">Expense Logs</h2>
            <div className="flex gap-2">
              <button className="px-3 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200" onClick={exportExpenseCsv}>Export CSV</button>
              <button className="px-3 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200" onClick={exportExpensePdf}>Export PDF</button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200">
              <thead>
                <tr className="bg-gray-50 text-left text-sm text-gray-600">
                  <th className="px-3 py-2 border">Title</th>
                  <th className="px-3 py-2 border">Category</th>
                  <th className="px-3 py-2 border">Amount</th>
                  <th className="px-3 py-2 border">Date</th>
                  <th className="px-3 py-2 border">Description</th>
                  <th className="px-3 py-2 border">Action</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((item) => (
                  <tr key={item._id} className="text-sm">
                    <td className="px-3 py-2 border">{item.title}</td>
                    <td className="px-3 py-2 border">{item.category}</td>
                    <td className="px-3 py-2 border">BDT {Number(item.amount || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 border">{item.date ? new Date(item.date).toLocaleDateString() : 'N/A'}</td>
                    <td className="px-3 py-2 border">{item.description || 'N/A'}</td>
                    <td className="px-3 py-2 border">
                      <div className="flex gap-2">
                        <button className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md" onClick={() => onEditExpense(item)}>Edit</button>
                        <button className="px-3 py-1 bg-red-100 text-red-700 rounded-md" onClick={() => setDeleteExpenseId(item._id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!expenses.length && (
                  <tr><td colSpan="6" className="px-3 py-8 border text-center text-gray-500">{loading ? 'Loading...' : 'No expenses found.'}</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-center items-center mt-6 space-x-4">
            <button onClick={() => setPage((prev) => Math.max(prev - 1, 1))} disabled={page === 1 || loading} className={`px-5 py-2 rounded-lg text-white font-semibold transition-all duration-300 ${page === 1 || loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600 active:scale-95'}`}>⬅ Previous</button>
            <span className="text-lg font-semibold text-gray-700">Page <span className="text-green-600">{page}</span> of <span className="text-green-600">{totalPages}</span></span>
            <button onClick={() => setPage((prev) => (prev < totalPages ? prev + 1 : prev))} disabled={page >= totalPages || loading} className={`px-5 py-2 rounded-lg text-white font-semibold transition-all duration-300 ${page >= totalPages || loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600 active:scale-95'}`}>Next ➡</button>
          </div>
        </div>
      </div>

      <ConfirmDialog isOpen={!!deleteExpenseId} onClose={() => setDeleteExpenseId('')} onConfirm={onDeleteExpense} title="Delete Expense" message="Are you sure you want to delete this expense?" />
    </Layout>
  );
}

export default SubscriptionExpenses;
